import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios'
import { QueryFunction } from 'react-query'

import { anonymisingResponseTransform } from '@percept/lorem'

import { SESSION_STORAGE_TOKEN_KEY } from '@percept/constants'

import { InfinitePaginationWrapper, Nullable, ServerPaginatedResponse } from '@percept/types'
import { isoDateUTC } from '@percept/utils'


const setAuthorizationHeader = (config: AxiosRequestConfig): AxiosRequestConfig => {
  config.headers.Authorization = `Bearer ${sessionStorage.getItem(SESSION_STORAGE_TOKEN_KEY)}`
  return config
}


export const makeAuthorizedApiClient = (instanceConfig: AxiosRequestConfig): AxiosInstance => {
  const apiClient = axios.create(instanceConfig)
  apiClient.interceptors.request.use(setAuthorizationHeader, (err) => Promise.reject(err))

  if( process.env.APP === 'demo-dashboard' ){
    apiClient.interceptors.response.use(anonymisingResponseTransform, (err) => Promise.reject(err))
  }

  return apiClient
}


export const apiClients = {
  core: makeAuthorizedApiClient({
    baseURL: `${process.env.CORE_API_ROOT}/v1`
  }),
  corePublic: axios.create({
    baseURL: `${process.env.CORE_API_ROOT}/v1`,
  }),
  performance: makeAuthorizedApiClient({
    baseURL: `${process.env.PERFORMANCE_API_ROOT}/v1`,
  }),
}


export const makeClientQueryFn = <T, R = T>(
  apiClient: AxiosInstance,
  requestConfig: AxiosRequestConfig,
  responseTransform?: (response: AxiosResponse<T>) => R
): QueryFunction<R> => {
  return async (): Promise<R> => {
    const response = await apiClient(requestConfig) as AxiosResponse<R>
    if( responseTransform ){
      return responseTransform(response as unknown as AxiosResponse<T>)
    }
    return response.data
  }
}


export const paginationAdapter = <T>(
  data: ServerPaginatedResponse<T>,
  page: number
): InfinitePaginationWrapper<T> => {
  const hasNext = page * data.size < data.total
  return {
    nextPage: hasNext ? page + 1 : undefined,
    previousPage: page > 1 ? page - 1 : undefined,
    items: data.items,
  }
}


export const getDateParams = <K extends string = 'start' | 'end'>({
  start,
  end,
  keys = ['start', 'end'] as [K, K]
}: (
  Nullable<Record<'start' | 'end', Date | null>>
  & {
    keys?: [K, K]
  }
)): Record<K, string | null> => {
  const [startKey, endKey] = keys
  return {
    [startKey]: start ? isoDateUTC(start) : null,
    [endKey]: end ? isoDateUTC(end) : null,
  } as Record<K, string | null>
}
